import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Battery, ChevronRight, Zap, Shield, Sparkles, Leaf, Phone,
  ClipboardList, CheckCircle, Wrench, Layers, Boxes, Gauge, Thermometer,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import SEO from '../components/SEO';
import { CQ7_INVERTERS, CQ7_PACKAGES, startingPrice, fmtAUD } from '../data/cq7Packages';

const HERO_IMG = '/product-images/fox-ess/battery-hero.jpg';

const FAQS = [
  { q: 'How does the Fox ESS CQ7 modular battery work?', a: 'The CQ7 is a high-voltage modular battery. You start with 1 Master module and add Slave modules to scale capacity from 6.96 kWh up to 48.72 kWh — so you only pay for the storage your home needs, and can expand later.' },
  { q: 'Do I need a single-phase or three-phase inverter?', a: "This depends on your property's electrical supply and overall system requirements. The CQ7 pairs with five Fox ESS hybrid inverter options across single and three-phase. Our team will confirm the right option for your home." },
  { q: 'Is the price final?', a: 'The advertised price is based on standard installation conditions. Additional costs may apply depending on site requirements, confirmed after the site inspection before installation is organised.' },
  { q: 'What does the site inspection check?', a: 'The switchboard, battery location, cable run, wall surface, base/slab suitability, and any additional installation requirements.' },
  { q: 'What happens if additional costs are required?', a: 'They will be confirmed after the site inspection and before installation is organised — so you always know your final price before we proceed.' },
  { q: 'Can I add more capacity later?', a: 'Yes. The CQ7 is modular — additional Slave modules can be stacked to increase capacity as your needs grow (subject to inverter compatibility).' },
  { q: 'Is the BSTC rebate included in the price?', a: 'The shown out-of-pocket price assumes the customer assigns the applicable BSTC (Battery STC) rebate to Supa Energy Hub.' },
];

const FoxESSPackagesPage = () => (
  <div className="min-h-screen">
    <SEO
      title="Fox ESS CQ7 Battery Packages — Fully Installed"
      description="Fox ESS CQ7 home battery packages from 6.96 kWh to 48.72 kWh — fully supplied and installed by Supa Energy Hub. Five Fox ESS hybrid inverter options (single & three-phase), transparent installed pricing after BSTC rebate."
      jsonLd={[
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        },
      ]}
    />
    <Header />
    <main>
      {/* HERO */}
      <section className="pt-28 pb-12 sm:pb-16 relative overflow-hidden bg-gradient-to-br from-[#f6f8f3] via-white to-[#eaf2dc]">
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="absolute top-20 right-0 w-72 h-72 bg-[#B5D43B]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-testid="foxess-main-breadcrumb">
            <Link to="/" className="hover:text-[#3D5A47]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/packages" className="hover:text-[#3D5A47]">Packages</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#3D5A47] font-medium">Fox ESS Battery Packages</span>
          </nav>
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 bg-[#B5D43B]/15 border border-[#B5D43B]/40 rounded-full px-3 py-1 mb-4">
                <Sparkles className="w-3.5 h-3.5 text-[#3D5A47]" />
                <span className="text-[11px] font-bold tracking-wider uppercase text-[#3D5A47]">Fox ESS CQ7 · Fully Supplied + Installed</span>
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#3D5A47] leading-[1.1] tracking-tight">
                Fox ESS <span className="text-[#B5D43B] bg-[#3D5A47] px-2 rounded-md">CQ7</span> Battery Packages
              </h1>
              <p className="text-base sm:text-lg text-gray-700 mt-5 max-w-2xl leading-relaxed">
                Store your solar and run your home through the evening with the Fox ESS CQ7 high-voltage modular battery.
                Choose a package from <strong>6.96 kWh up to 48.72 kWh</strong> — fully supplied, installed and rebate-handled,
                at one transparent installed price.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a href="#packages">
                  <Button className="bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 text-sm sm:text-base font-semibold px-5 py-5 rounded-xl shadow-lg shadow-[#3D5A47]/20" data-testid="foxess-main-cta">
                    Compare Fox ESS Packages <ArrowRight className="w-4 h-4" />
                  </Button>
                </a>
                <Link to="/get-estimate?source=foxess-main">
                  <Button variant="outline" className="bg-white border-[#3D5A47]/20 text-[#3D5A47] hover:bg-[#f8faf5] gap-2 text-sm sm:text-base px-5 py-5 rounded-xl">
                    <ClipboardList className="w-4 h-4" /> Get My Fox ESS Quote
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-gradient-to-br from-[#1a2820] to-[#3D5A47] rounded-2xl shadow-xl overflow-hidden p-6 flex items-center justify-center">
                <img src={HERO_IMG} alt="Fox ESS CQ7 high-voltage modular home battery" className="w-full h-auto max-h-[360px] object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT FOX ESS */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">The brand</span>
            <h2 className="heading-2 text-[#3D5A47] mb-4">Who is Fox ESS?</h2>
            <p className="text-gray-700 text-sm leading-relaxed mb-4">
              Fox ESS is a global energy-storage and inverter manufacturer with strong distribution and after-sales support
              across Australia, and a product portfolio purpose-built for the local single- and three-phase residential market.
            </p>
            <p className="text-gray-700 text-sm leading-relaxed">
              The <strong>CQ7</strong> is Fox ESS&rsquo;s flagship high-voltage modular battery — pairing safe LFP cells with
              plug-and-play installation, 100% usable energy and IP65 outdoor protection, ready for the federal Cheaper Home
              Batteries Program and applicable state battery rebates*.
            </p>
            <Link to="/brands/fox-ess" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B]">
              View Fox ESS brand &amp; full specs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: Battery, t: '100% Usable DOD', d: 'Every stored kWh is yours to use — no derated capacity.' },
              { icon: Leaf, t: 'Safe LFP chemistry', d: 'Lithium iron phosphate — long cycle life, thermally stable.' },
              { icon: Boxes, t: 'Modular & scalable', d: 'Stack Master + Slave modules from 6.96 to 48.72 kWh.' },
              { icon: Shield, t: 'IP65 outdoor-rated', d: 'Dust-tight and weather sealed — built to live outside.' },
            ].map((f, i) => (
              <div key={i} className="bg-[#f8faf5] rounded-xl p-5 border border-gray-100">
                <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center mb-3"><f.icon className="w-5 h-5 text-[#3D5A47]" /></div>
                <h3 className="font-bold text-sm text-[#3D5A47] mb-1">{f.t}</h3>
                <p className="text-xs text-gray-600">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW THE MODULAR SYSTEM WORKS */}
      <section className="py-16 bg-[#f8faf5] border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Fox ESS CQ7 battery</span>
          <h2 className="heading-2 text-[#3D5A47] mb-3">How the modular battery system works</h2>
          <p className="text-gray-700 text-sm max-w-3xl leading-relaxed mb-8">
            The CQ7 is built from stackable high-voltage modules. You start with one <strong>Master</strong> module and add
            <strong> Slave</strong> modules to reach the capacity your home needs — so your storage grows in clean, predictable
            steps from 6.96 kWh all the way to 48.72 kWh.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <div className="flex items-center gap-2 mb-2"><Layers className="w-5 h-5 text-[#3D5A47]" /><h3 className="font-bold text-sm text-[#3D5A47]">Master + Slave stacking</h3></div>
              <p className="text-xs text-gray-600">1 Master powers the stack; each added Slave adds capacity — up to 48.72 kWh in our standard packages.</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <div className="flex items-center gap-2 mb-2"><Gauge className="w-5 h-5 text-[#3D5A47]" /><h3 className="font-bold text-sm text-[#3D5A47]">80 A high current</h3></div>
              <p className="text-xs text-gray-600">Higher continuous current than the CQ6 — comfortably supports larger inverter loads and faster charge/discharge.</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <div className="flex items-center gap-2 mb-2"><Thermometer className="w-5 h-5 text-[#3D5A47]" /><h3 className="font-bold text-sm text-[#3D5A47]">Built for Aussie homes</h3></div>
              <p className="text-xs text-gray-600">Broad operating-temperature tolerance and a published inverter-compatibility matrix for single- and three-phase homes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* INVERTER OPTIONS (NO PRICES) */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Inverter options</span>
          <h2 className="heading-2 text-[#3D5A47] mb-3">Fox ESS hybrid inverter range</h2>
          <p className="text-gray-700 text-sm max-w-3xl leading-relaxed mb-6">
            Every CQ7 package pairs the battery with a Fox ESS hybrid inverter. Choose a single-phase or three-phase inverter
            to suit your property&rsquo;s electrical supply and solar size. Pricing for each option is shown on the individual
            package pages.
          </p>
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm" data-testid="foxess-inverter-options-table">
            <table className="w-full text-sm">
              <thead className="bg-[#3D5A47] text-white text-left">
                <tr>
                  <th className="px-4 sm:px-6 py-3 font-semibold">Inverter Option</th>
                  <th className="px-4 sm:px-6 py-3 font-semibold">Phase</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {CQ7_INVERTERS.map((inv) => (
                  <tr key={inv.id}>
                    <td className="px-4 sm:px-6 py-3.5">
                      <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#3D5A47]" /><span className="font-semibold text-[#3D5A47]">{inv.short}</span></div>
                    </td>
                    <td className="px-4 sm:px-6 py-3.5 text-gray-600">{inv.id.includes('3p') ? 'Three-Phase' : 'Single-Phase'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="py-16 bg-[#f8faf5] border-y border-gray-100 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Battery package options</span>
          <h2 className="heading-2 text-[#3D5A47] mb-2">Fox ESS CQ7 packages from 6.96 kWh to 48.72 kWh</h2>
          <p className="text-gray-700 text-sm max-w-3xl mb-8">
            Every price below is your installed cost after the BSTC rebate. Tap a package to see full inverter pricing,
            inclusions and the additional-costs details.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="foxess-packages-grid">
            {CQ7_PACKAGES.map((p) => (
              <Link
                key={p.slug}
                to={`/packages/${p.slug}`}
                className="group bg-white rounded-2xl border-2 border-[#B5D43B]/20 hover:border-[#B5D43B] hover:shadow-xl transition-all overflow-hidden flex flex-col"
                data-testid={`foxess-card-${p.slug}`}
              >
                <div className="bg-gradient-to-br from-[#3D5A47] to-[#1a2820] p-5 text-white relative overflow-hidden">
                  <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #B5D43B 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      <Battery className="w-4 h-4 text-[#B5D43B]" />
                      <span className="text-[10px] uppercase tracking-widest text-[#B5D43B] font-bold">Fox ESS CQ7</span>
                    </div>
                    <p className="text-4xl font-black leading-none">{p.kwh}<span className="text-lg text-white/60"> kWh</span></p>
                    <p className="text-xs text-white/70 mt-2">{p.config}</p>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Starting from</p>
                  <p className="text-3xl font-black text-[#3D5A47] leading-tight mt-0.5">{fmtAUD(startingPrice(p))}</p>
                  <p className="text-[11px] text-gray-500 mt-1">installed after BSTC rebate*</p>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1 text-gray-600"><Zap className="w-3 h-3" /> 5 inverter options</span>
                    <span className="inline-flex items-center gap-1 text-[#3D5A47] font-semibold group-hover:text-[#B5D43B]">View <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-6">
            *Prices assume standard installation and customer assignment of the applicable BSTC rebate to Supa Energy Hub. Additional site-specific costs may apply.
          </p>
        </div>
      </section>

      {/* STANDARD INSTALL + ADDITIONAL COSTS + SITE INSPECTION */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="bg-[#f8faf5] rounded-2xl border border-gray-100 p-6 sm:p-7">
            <h2 className="heading-2 text-[#3D5A47] mb-2 flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#B5D43B]" /> Standard installation</h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              Prices are based on standard installation conditions and customer assignment of the applicable BSTC rebate to
              Supa Energy Hub. Final price may vary depending on site conditions, switchboard requirements, battery location,
              cable run, wall surface, base/slab condition, safety requirements, and other installation factors.
            </p>
          </div>
          <div className="bg-[#fdf4ec] rounded-2xl border border-orange-100 p-6 sm:p-7">
            <h2 className="heading-2 text-[#3D5A47] mb-2 flex items-center gap-2"><Wrench className="w-5 h-5 text-orange-600" /> Additional costs that may apply</h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              <strong>Important Note:</strong> Additional costs may apply if required, including bollards at $200 per site,
              awning at $200 per site, additional cabling beyond 5 m at $25 per metre, concrete slab/base at $150, fireproof
              backing/cement sheet at $150, additional switchboard works, obstructed cabling pathway, or other site-specific
              labour and materials. These will be confirmed after the site inspection before installation is organised.
            </p>
          </div>
          <div className="bg-[#f8faf5] rounded-2xl border border-gray-100 p-6 sm:p-7">
            <h2 className="heading-2 text-[#3D5A47] mb-2 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#3D5A47]" /> Site inspection deposit</h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              Non-refundable deposit for a technician site inspection to confirm installation suitability and identify any
              additional costs, if required. If you proceed, this amount is deducted from your final balance payable. If you
              cancel after the inspection or after receiving any additional cost estimate, this amount is retained as the site
              inspection / cancellation fee.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#f8faf5] border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">FAQ</span>
          <h2 className="heading-2 text-[#3D5A47] mb-6">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="foxess-main-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-[#3D5A47] font-semibold text-sm hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-gray-600 text-sm">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#3D5A47] to-[#1a2820] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-3">Find your Fox ESS CQ7 package</h2>
          <p className="text-white/80 mb-6">Free quote. We handle the BSTC rebate paperwork and install everything end-to-end.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/get-estimate?source=foxess-main">
              <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#1a2820] gap-2 font-bold px-6 py-5 rounded-xl" data-testid="foxess-main-cta-quote">
                <ClipboardList className="w-4 h-4" /> Get My Fox ESS Battery Quote
              </Button>
            </Link>
            <a href="tel:0478453548">
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 gap-2 px-6 py-5 rounded-xl">
                <Phone className="w-4 h-4" /> Call 0478 453 548
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default FoxESSPackagesPage;
